import { Link } from "lucide-react";
import { FC, useState } from "react";
import { useFormContext } from "react-hook-form";

import BuilderIcon from "@/assets/logos/builder.svg?react";
import LegitimateIcon from "@/assets/logos/LogoSymbol.svg?react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useUpdateSKUMutation } from "@/data/mutations";
import { SKUPageType } from "@/data/types";
import { useFeatureFlag } from "@/hooks/use-feature-flags";
import { ACCESS_LEGITIMATE_CMS } from "@/lib/features";
import { SKUFormValues } from "@/lib/forms";

type Props = {
  skuId: string;
  pageType: SKUPageType;
};

export const ChooseBuilderDialog: FC<Props> = ({ skuId, pageType }) => {
  const form = useFormContext<SKUFormValues>();
  const updateSKUMutation = useUpdateSKUMutation(skuId);
  const shouldSelectCMS = useFeatureFlag(ACCESS_LEGITIMATE_CMS);

  const [open, setOpen] = useState(false);

  const onSubmit = async (values: SKUFormValues) => {
    try {
      await updateSKUMutation.mutateAsync({
        page_type: values.page_type,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" type="button">
          Change webpage builder
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose a webpage builder</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Choose a webpage builder to use for this SKU.
        </DialogDescription>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            name="page_type"
            control={form.control}
            defaultValue={pageType}
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormControl>
                  <RadioGroup
                    name="page_type"
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-col gap-1"
                  >
                    {shouldSelectCMS && (
                      <FormItem className="flex items-start px-2 py-2 space-x-2 rounded-md hover:bg-gray-50">
                        <FormControl>
                          <RadioGroupItem value="legitimate" />
                        </FormControl>
                        <FormLabel className="font-normal w-full flex gap-2">
                          <LegitimateIcon className="size-4 pointer-events-none" />
                          <div>
                            Legitimate
                            <FormDescription className="mt-1">
                              Build the digital experience using Legitimate
                            </FormDescription>
                          </div>
                        </FormLabel>
                      </FormItem>
                    )}
                    <FormItem className="flex items-start px-2 py-2 space-x-2 rounded-md hover:bg-gray-50">
                      <FormControl>
                        <RadioGroupItem value="builder_io" />
                      </FormControl>
                      <FormLabel className="font-normal w-full flex gap-2">
                        <BuilderIcon className="size-4 pointer-events-none" />
                        <div>
                          Builder.io
                          <FormDescription className="mt-1">
                            Integrate with Builder.io, a third-party tool
                          </FormDescription>
                        </div>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-start px-2 py-2 space-x-2 rounded-md hover:bg-gray-50">
                      <FormControl>
                        <RadioGroupItem value="redirect" />
                      </FormControl>
                      <FormLabel className="font-normal w-full flex gap-2">
                        <Link className="size-4 pointer-events-none" />
                        <div>
                          Redirect
                          <FormDescription className="mt-1">
                            Reroute to a different URL with tag parameters
                          </FormDescription>
                        </div>
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit">Submit</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
